import { creatorbase } from '@round/api';
import { ComboboxItem } from '@round/creatorbase-ui';
import { usePoolsListInfinite } from '@round/creatorbase-helpers/api-hooks';
import debounce from 'lodash/debounce';
import { UIEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { isNumber } from 'utility/utility';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 500;
const LIST_SCROLL_THRESHOLD_PX = 80;

/** Puts the pool the user last worked on at the top of the list, and so under the sheet's default. */
const ORDERING: creatorbase.PoolOrdering = '-last_edited_at';

export type PoolItem = ComboboxItem<number>;

export const mapPoolToItem = (pool: creatorbase.Pool): PoolItem => ({
    value: pool.id,
    label: pool.name,
});

type PoolListParams = Omit<creatorbase.GetPoolsParams, 'page'>;

const getPoolListParams = (clientId: number | undefined, search: string): PoolListParams => ({
    client_id: clientId,
    page_size: PAGE_SIZE,
    ordering: ORDERING,
    search,
});

/**
 * The sheet opens on the first pool of the unsearched list, so the query behind that default must
 * not carry the combobox's search — otherwise typing would swap the pool out from under the user.
 *
 * Exported because the campaigns table prefetches this exact key when the add-creators menu opens.
 * Any divergence warms a cache entry the sheet never reads.
 */
export const getDefaultPoolSelectParams = (clientId: number | undefined) => getPoolListParams(clientId, '');

type Params = {
    clientId: number | undefined;
    isEnabled: boolean;
};

export default function usePoolCombobox({ clientId, isEnabled }: Params) {
    const [isListOpen, setIsListOpen] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const setSearch = useMemo(() => debounce(setDebouncedSearch, SEARCH_DEBOUNCE_MS), []);

    // The sheet unmounts on every campaign change, so a keystroke can still be pending on the way out.
    useEffect(() => () => setSearch.cancel(), [setSearch]);

    const {
        data: pools,
        isLoading,
        hasError,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = usePoolsListInfinite(getPoolListParams(clientId, debouncedSearch), {
        enabled: isNumber(clientId) && isEnabled,
    });

    const onInputValueChange = useCallback((value: string) => setSearch(value), [setSearch]);

    // The search input lives inside the popup, and base-ui empties it on close. The term behind the
    // list has to clear with it, or reopening shows a filtered list under an empty input.
    const handleOpenChange = useCallback(
        (open: boolean) => {
            setIsListOpen(open);
            if (!open) {
                setSearch.cancel();
                setDebouncedSearch('');
            }
        },
        [setSearch]
    );

    const onItemsListScroll = useCallback<UIEventHandler<HTMLDivElement>>(
        (event) => {
            const element = event.currentTarget;
            if (element.scrollHeight - element.scrollTop - element.clientHeight > LIST_SCROLL_THRESHOLD_PX) {
                return;
            }

            if (hasNextPage && !isFetchingNextPage) {
                void fetchNextPage();
            }
        },
        [fetchNextPage, hasNextPage, isFetchingNextPage]
    );

    return {
        onItemsListScroll,
        noItemsFound: getNoItemsFoundMessage({ isLoading, hasError }),
        comboboxProps: {
            items: pools.map(mapPoolToItem),
            open: isListOpen,
            onOpenChange: handleOpenChange,
            onInputValueChange,
            /** Disables base-ui client filtering; the list is driven by the debounced API search. */
            filter: null,
        },
    };
}

const getNoItemsFoundMessage = ({ isLoading, hasError }: { isLoading: boolean; hasError: boolean }) => {
    if (hasError) {
        return 'Could not get pools';
    }

    if (isLoading) {
        return 'Loading pools…';
    }

    return 'No pools found';
};
